import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Content,
  Panel,
  Button,
  Badge,
  Message,
  Modal,
  FlexboxGrid,
  Loader,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import NavigationBar from '../Shared/NavigationBar';
import CreateCollectionModal from './CreateCollectionModal';
import ImportCSVModal from './ImportCSVModal';
import ImportDeckModal from './ImportDeckModal';
import CollectionCard from './CollectionCard';
import FolderManagementModal from './FolderManagementModal';
import MoveDeckToFolderModal from './MoveDeckToFolderModal';
import authFetch from '../../helpers/authFetch';
import { getCardImageUrl } from '../../helpers/cardImageHelper';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import { formatContainerCount } from '../../helpers/containerCountLabel';

const BADGE_COLORS = {
  DECK: '#3498db',
  TRADE_BINDER: '#2ecc71',
  WISHLIST: '#9b59b6',
};

const BADGE_LABELS = {
  DECK: 'Deck',
  TRADE_BINDER: 'Trade Binder',
  WISHLIST: 'Wish List',
};

const SPACING = {
  containerMaxWidth: 900,
  containerPadding: 16,
  containerMarginTop: 30,
  headerMarginBottom: 24,
  cardMarginBottom: 12,
  cardPadding: 16,
  errorMarginBottom: 16,
  emptyPadding: 60,
  descriptionMarginTop: 8,
  actionsGap: 12,
  badgeMarginLeft: 8,
  grandStatsGap: 24,
  grandBorderWidth: 3,
};

const FONT = {
  collectionName: 16,
  badgeLabel: 12,
  deckType: 12,
  emptyTitle: 18,
  description: 13,
  grandTitle: 18,
  grandStatLabel: 12,
  grandStatValue: 22,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    muted: t.textMuted,
    grandGradientStart: t.backgroundPrimary,
    grandGradientEnd: t.backgroundSecondary,
    grandBorder: t.accentSecondary,
    grandLabel: t.accentPrimary,
    grandValue: t.textPrimary,
  };
};

const STATUS_COLORS = {
  pending: '#6c757d',
  valid: '#28a745',
  invalid: '#dc3545',
};

const Dashboard = () => {
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);

  const [collections, setCollections] = useState([]);
  const [sharedCollections, setSharedCollections] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeckImportModal, setShowDeckImportModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [moveDeckTarget, setMoveDeckTarget] = useState(null);
  const [validationCache, setValidationCache] = useState({});
  const [grandSummary, setGrandSummary] = useState(null);
  const [previewCardImages, setPreviewCardImages] = useState({});

  const validatingRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const previewCardCacheRef = useRef(new Map());

  const navigate = useNavigate();
  const toaster = useToaster();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type');

  const fetchCollections = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch owned collections, shared-with-me, and folders in parallel
      const [ownedResp, sharedResp, foldersResp] = await Promise.all([
        authFetch('/collections'),
        authFetch('/collections/shared-with-me'),
        authFetch('/folders'),
      ]);

      if (!ownedResp.ok) {
        setError('Failed to load collections');
        return;
      }

      const ownedData = await ownedResp.json();
      setCollections(ownedData);

      // Fetch shared collections (errors are non-fatal)
      if (sharedResp.ok) {
        const sharedData = await sharedResp.json();
        setSharedCollections(sharedData);
      } else {
        setSharedCollections([]);
      }

      // Fetch folders (errors are non-fatal)
      if (foldersResp.ok) {
        const foldersData = await foldersResp.json();
        setFolders(foldersData);
      } else {
        setFolders([]);
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchGrandSummary = async () => {
    try {
      const res = await authFetch('/collections/grand?limit=1');
      if (res.ok) {
        const data = await res.json();
        setGrandSummary({
          totalUnique: data.pagination?.total || 0,
        });
      }
    } catch (err) {
      // non-fatal — grand summary is optional
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchGrandSummary();
  }, []);

  // Fetch preview card images for collections that have a preview card set
  useEffect(() => {
    if (collections.length === 0) return;

    const collectionsWithPreview = collections.filter(c => c.preview_card_scryfall_id);

    collectionsWithPreview.forEach(collection => {
      const scryfallId = collection.preview_card_scryfall_id;

      if (previewCardCacheRef.current.has(scryfallId)) {
        const cachedUrl = previewCardCacheRef.current.get(scryfallId);
        setPreviewCardImages(prev => ({
          ...prev,
          [collection.id]: cachedUrl,
        }));
        return;
      }

      authFetch(`/cards/${scryfallId}`)
        .then(res => res.ok ? res.json() : null)
        .then(card => {
          if (!isMountedRef.current) return;

          const imageUrl = getCardImageUrl(card, true);
          if (imageUrl) {
            previewCardCacheRef.current.set(scryfallId, imageUrl);
            setPreviewCardImages(prev => ({
              ...prev,
              [collection.id]: imageUrl,
            }));
          }
        })
        .catch(err => {
          if (isMountedRef.current) {
            console.warn(`Failed to fetch preview card ${scryfallId}:`, err);
          }
        });
    });

    return () => {
      isMountedRef.current = false;
    };
  }, [collections]);

  // Validate DECK collections sequentially when collections load or change
  useEffect(() => {
    if (collections.length === 0) return;
  
    const validateDecks = async () => {
      for (const collection of collections) {
        // Only validate DECK type, not TRADE_BINDER or OTHER format
        if (collection.type !== 'DECK' || collection.deck_type === 'OTHER') {
          continue;
        }
  
        // Skip if already validating or already cached
        if (validatingRef.current.has(collection.id) || validationCache[collection.id]) {
          continue;
        }
  
        // Mark as validating
        validatingRef.current.add(collection.id);
  
        try {
          const response = await authFetch(`/collections/${collection.id}/validate`);
          if (response.ok) {
            const data = await response.json();
            if (isMountedRef.current) {
              setValidationCache(prev => ({
                ...prev,
                [collection.id]: data,
              }));
            }
          } else {
            // Set error state for failed validation (server error, not network failure)
            if (isMountedRef.current) {
              setValidationCache(prev => ({
                ...prev,
                [collection.id]: { valid: false, errors: [], warnings: [] },
              }));
            }
          }
        } catch (err) {
          console.error(`Failed to validate collection ${collection.id}:`, err);
          // Set distinct error state on network failure
          if (isMountedRef.current) {
            setValidationCache(prev => ({
              ...prev,
              [collection.id]: { error: true },
            }));
          }
        } finally {
          validatingRef.current.delete(collection.id);
        }
  
        // Small delay between sequential validations to avoid overwhelming the server
        await new Promise(r => setTimeout(r, 100));
      }
    };
  
    validateDecks();
  
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections]);

  const toggleFolderExpanded = (folderId) => {
    setExpandedFolders(prev => {
      const updated = new Set(prev);
      if (updated.has(folderId)) {
        updated.delete(folderId);
      } else {
        updated.add(folderId);
      }
      return updated;
    });
  };

  const handleFolderDragStart = (e, folderId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', folderId);
  };

  const handleFolderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDrop = async (e, targetFolderId) => {
    e.preventDefault();
    const draggedFolderId = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (draggedFolderId === targetFolderId) return;

    const draggedIndex = folders.findIndex(f => f.id === draggedFolderId);
    const targetIndex = folders.findIndex(f => f.id === targetFolderId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Create new order by swapping
    const newFolders = [...folders];
    [newFolders[draggedIndex], newFolders[targetIndex]] = [newFolders[targetIndex], newFolders[draggedIndex]];

    // Update sort_order values
    const updatedFolders = newFolders.map((folder, index) => ({
      ...folder,
      sort_order: index,
    }));

    setFolders(updatedFolders);

    // Persist to server
    try {
      const payload = {
        folders: updatedFolders.map(f => ({ id: f.id, sort_order: f.sort_order })),
      };
      console.log('Sending folder reorder:', payload);

      const response = await authFetch('/folders/sort-order', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('Folder reorder response:', response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Failed to update folder order:', response.status, errorData);
        // Revert on error
        setFolders(folders);
      } else {
        const result = await response.json();
        console.log('Folder reorder succeeded:', result);
      }
    } catch (err) {
      console.error('Failed to update folder order:', err);
      // Revert on error
      setFolders(folders);
    }
  };

  const handleDeckMoved = (updatedDeck) => {
    setCollections(prev =>
      prev.map(c => (c.id === updatedDeck.id ? { ...c, ...updatedDeck } : c))
    );

    // Auto-expand the folder the deck was moved to
    if (updatedDeck.folder_id) {
      setExpandedFolders(prev => new Set(prev).add(updatedDeck.folder_id));
    }

    setMoveDeckTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await authFetch(`/collections/${deleteTarget.id}`, {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        setCollections((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      } else {
        setError('Failed to delete collection');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const typeBadge = (type) => (
    <span
      style={{
        backgroundColor: BADGE_COLORS[type],
        color: '#fff',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: FONT.badgeLabel,
        marginLeft: SPACING.badgeMarginLeft,
      }}
    >
      {BADGE_LABELS[type]}
    </span>
  );

  const legalityBadge = (collection) => {
    // Only show badge for DECK type, not OTHER format
    if (collection.type !== 'DECK' || collection.deck_type === 'OTHER') {
      return null;
    }
  
    const validation = validationCache[collection.id];
  
    // Not yet validated (pending)
    if (!validation) {
      return (
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Checking deck validity...</Tooltip>}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: STATUS_COLORS.pending,
              color: '#fff',
              fontSize: 12,
              fontWeight: 'bold',
              marginLeft: SPACING.badgeMarginLeft,
            }}
          >
            ?
          </span>
        </Whisper>
      );
    }
  
    // Validation error (network failure or server error)
    if (validation.error) {
      return (
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Unable to validate deck</Tooltip>}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: STATUS_COLORS.pending,
              color: '#fff',
              fontSize: 12,
              fontWeight: 'bold',
              marginLeft: SPACING.badgeMarginLeft,
            }}
          >
            ?
          </span>
        </Whisper>
      );
    }
  
    // Empty deck check
    if (collection.card_count === 0) {
      return (
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Collection is empty</Tooltip>}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: STATUS_COLORS.pending,
              color: '#fff',
              fontSize: 12,
              fontWeight: 'bold',
              marginLeft: SPACING.badgeMarginLeft,
            }}
          >
            ?
          </span>
        </Whisper>
      );
    }
  
    // Valid deck
    if (validation.valid) {
      return (
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Legal</Tooltip>}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: STATUS_COLORS.valid,
              color: '#fff',
              fontSize: 14,
              fontWeight: 'bold',
              marginLeft: SPACING.badgeMarginLeft,
            }}
          >
            ✓
          </span>
        </Whisper>
      );
    }
  
    // Invalid deck (has errors)
    const totalIssues = (validation.errors?.length || 0) + (validation.warnings?.length || 0);
    const tooltipText = totalIssues === 1 ? '1 issue found' : `${totalIssues} issues found`;
  
    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>{tooltipText}</Tooltip>}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: STATUS_COLORS.invalid,
            color: '#fff',
            fontSize: 14,
            fontWeight: 'bold',
            marginLeft: SPACING.badgeMarginLeft,
          }}
        >
          ✕
        </span>
      </Whisper>
    );
  };

  const visibilityBadge = (visibility) => {
    if (visibility === 'PRIVATE') return null; // PRIVATE has no badge
    
    const badgeConfig = {
      INVITE_ONLY: { label: 'Shared', color: '#6c757d' },
      PUBLIC: { label: 'Public', color: '#17a2b8' },
    };

    const config = badgeConfig[visibility];
    if (!config) return null;

    const tooltipText = visibility === 'INVITE_ONLY' 
      ? 'Visible to invited users'
      : 'Visible to anyone with the link';

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>{tooltipText}</Tooltip>}
      >
        <span
          style={{
            backgroundColor: config.color,
            color: '#fff',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: FONT.badgeLabel,
            marginLeft: SPACING.badgeMarginLeft,
          }}
        >
          {config.label}
        </span>
      </Whisper>
    );
  };

  const styles = {
    container: {
      maxWidth: SPACING.containerMaxWidth,
      margin: `${SPACING.containerMarginTop}px auto`,
      padding: `0 ${SPACING.containerPadding}px`,
    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? SPACING.cardMarginBottom : 0,
      marginBottom: SPACING.headerMarginBottom,
    },
    card: { cursor: 'pointer', marginBottom: SPACING.cardMarginBottom },
    cardHeader: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? SPACING.cardMarginBottom : 0,
    },
    cardTitle: { display: 'flex', alignItems: 'center', flexWrap: 'wrap' },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.actionsGap,
      marginTop: 12,
    },
    empty: {
      textAlign: 'center',
      padding: `${SPACING.emptyPadding}px 0`,
      color: COLORS.muted,
    },
  };

  return (
    <>
      <NavigationBar />
      <Container>
        <Content>
          <div style={styles.container}>
            <div style={styles.header}>
              <h3>My Library</h3>
              <div style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  appearance="primary"
                  block={isMobile}
                  onClick={() => setShowCreateModal(true)}
                >
                  Create New
                </Button>
                <Button
                  appearance="ghost"
                  block={isMobile}
                  onClick={() => setShowDeckImportModal(true)}
                >
                  Import from deck list
                </Button>
                <Button
                  appearance="ghost"
                  block={isMobile}
                  onClick={() => setShowImportModal(true)}
                >
                  Import CSV
                </Button>
                <Button
                  appearance="ghost"
                  block={isMobile}
                  onClick={() => setShowFolderModal(true)}
                >
                  Manage Folders
                </Button>
              </div>
            </div>

            {error && (
              <Message type="error" showIcon style={{ marginBottom: SPACING.errorMarginBottom }}>
                {error}
              </Message>
            )}

            {loading ? (
              <Loader center size="md" content="Loading..." />
            ) : (() => {
              const TYPE_MAP = { DECK: 'DECK', TRADE_BINDER: 'TRADE_BINDER', WISHLIST: 'WISHLIST' };
              const activeType = TYPE_MAP[typeFilter] || null;
              const visibleCollections = activeType
                ? collections.filter(c => c.type === activeType)
                : collections.filter(c => c.type !== 'COLLECTION');
              const emptyLabel = activeType === 'DECK' ? 'No decks yet'
                : activeType === 'TRADE_BINDER' ? 'No binders yet'
                : activeType === 'WISHLIST' ? 'No wish lists yet'
                : 'No decks, binders, or wish lists yet';
              const emptyHint = activeType === 'DECK' ? 'Create your first deck to start tracking your cards.'
                : activeType === 'TRADE_BINDER' ? 'Create your first binder to start tracking your cards.'
                : activeType === 'WISHLIST' ? 'Create your first wish list to track cards you want to acquire.'
                : 'Create your first deck, binder, or wish list to start tracking your cards.';

              if (visibleCollections.length === 0) return (
                <div style={styles.empty}>
                  <p style={{ fontSize: FONT.emptyTitle, marginBottom: SPACING.badgeMarginLeft }}>
                    {emptyLabel}
                  </p>
                  <p>{emptyHint}</p>
                </div>
              );

              return (
              <>
              {grandSummary && !activeType && (
                <Panel
                  bordered
                  bodyFill
                  onClick={() => navigate('/collection')}
                  style={{
                    cursor: 'pointer',
                    marginBottom: SPACING.cardPadding,
                    padding: SPACING.cardPadding,
                    background: `linear-gradient(135deg, ${COLORS.grandGradientStart}, ${COLORS.grandGradientEnd})`,
                    borderLeft: `${SPACING.grandBorderWidth}px solid ${COLORS.grandBorder}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: SPACING.actionsGap }}>
                    <span style={{ fontSize: FONT.grandTitle, fontWeight: 700, color: COLORS.grandValue }}>
                      Collection
                    </span>
                    <div style={{ display: 'flex', gap: SPACING.grandStatsGap, flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: FONT.grandStatLabel, color: COLORS.grandLabel }}>Unique Cards</div>
                        <div style={{ fontSize: FONT.grandStatValue, fontWeight: 'bold', color: COLORS.grandValue }}>{grandSummary.totalUnique}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: FONT.grandStatLabel, color: COLORS.grandLabel }}>Total Cards</div>
                        <div style={{ fontSize: FONT.grandStatValue, fontWeight: 'bold', color: COLORS.grandValue }}>
                          {collections.reduce((sum, c) => sum + (c.card_count || 0), 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Panel>
              )}

              <>
                {(() => {
                  const collectionsByFolder = {};
                  const unfoldered = [];

                  visibleCollections.forEach(collection => {
                    if (collection.folder_id) {
                      if (!collectionsByFolder[collection.folder_id]) {
                        collectionsByFolder[collection.folder_id] = [];
                      }
                      collectionsByFolder[collection.folder_id].push(collection);
                    } else {
                      unfoldered.push(collection);
                    }
                  });

                  const foldersSorted = folders.sort((a, b) => a.sort_order - b.sort_order);

                  return (
                    <>
                      {foldersSorted.map(folder => {
                        const collectionsInFolder = collectionsByFolder[folder.id] || [];
                        const isExpanded = expandedFolders.has(folder.id);

                        return (
                          <div key={`folder-${folder.id}`} style={{ marginBottom: 16 }}>
                            <div
                              draggable
                              onDragStart={(e) => handleFolderDragStart(e, folder.id)}
                              onDragOver={handleFolderDragOver}
                              onDrop={(e) => handleFolderDrop(e, folder.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                backgroundColor: COLORS.grandGradientStart,
                                borderRadius: 4,
                                cursor: 'grab',
                                userSelect: 'none',
                              }}
                              onClick={() => toggleFolderExpanded(folder.id)}
                            >
                              <span style={{ fontSize: 18, marginRight: 12 }}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                              <span style={{ marginRight: 8 }}>📁</span>
                              <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                                {folder.name}
                              </span>
                              <Badge content={`${collectionsInFolder.length} decks`} />
                            </div>

                            {isExpanded && (
                              <div style={{ marginTop: 8, marginLeft: 8 }}>
                                <FlexboxGrid>
                                  {collectionsInFolder.map((collection) => (
                                    <FlexboxGrid.Item colspan={24} key={collection.id} style={styles.card}>
                                      <CollectionCard
                                        collection={collection}
                                        previewImageUrl={previewCardImages[collection.id]}
                                        onDelete={setDeleteTarget}
                                        onMove={setMoveDeckTarget}
                                        showMoveButton={folders.length > 0}
                                        typeBadge={typeBadge}
                                        visibilityBadge={visibilityBadge}
                                        legalityBadge={legalityBadge}
                                        styles={styles}
                                        FONT={FONT}
                                        COLORS={COLORS}
                                        SPACING={SPACING}
                                      />
                                    </FlexboxGrid.Item>
                                  ))}
                                </FlexboxGrid>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {unfoldered.length > 0 && (
                        <FlexboxGrid style={{ marginTop: 16 }}>
                          {unfoldered.map((collection) => (
                            <FlexboxGrid.Item colspan={24} key={collection.id} style={styles.card}>
                              <CollectionCard
                                collection={collection}
                                previewImageUrl={previewCardImages[collection.id]}
                                onDelete={setDeleteTarget}
                                onMove={setMoveDeckTarget}
                                showMoveButton={true}
                                typeBadge={typeBadge}
                                visibilityBadge={visibilityBadge}
                                legalityBadge={legalityBadge}
                                styles={styles}
                                FONT={FONT}
                                COLORS={COLORS}
                                SPACING={SPACING}
                              />
                            </FlexboxGrid.Item>
                          ))}
                        </FlexboxGrid>
                      )}
                    </>
                  );
                })()}
              </>
              </>
              ); })()}

            {sharedCollections.length > 0 && (
              <>
                <h2 style={{ marginTop: 32, marginBottom: 16, fontSize: 18 }}>Shared with Me</h2>
                <FlexboxGrid>
                  {sharedCollections.map((collection) => (
                    <FlexboxGrid.Item colspan={24} key={`shared-${collection.id}`} style={styles.card}>
                      <Panel
                        bordered
                        bodyFill
                        onClick={() => navigate(`/shared/${collection.share_slug}`)}
                        style={{ padding: SPACING.cardPadding, opacity: 0.9 }}
                      >
                        <div style={styles.cardHeader}>
                          <div style={styles.cardTitle}>
                            <span style={{ fontSize: FONT.collectionName, fontWeight: 600 }}>
                              {collection.name}
                            </span>
                            {typeBadge(collection.type)}
                            {collection.deck_type && (
                              <span style={{ fontSize: FONT.deckType, color: COLORS.muted, marginLeft: SPACING.badgeMarginLeft }}>
                                {collection.deck_type}
                              </span>
                            )}
                          </div>
                          <div style={styles.actions}>
                            <Badge content={formatContainerCount(collection)} />
                          </div>
                        </div>
                        <p style={{ marginTop: SPACING.descriptionMarginTop, color: COLORS.muted, fontSize: FONT.description }}>
                          Shared by <strong>{collection.owner_username}</strong>
                        </p>
                        {collection.description && (
                          <p style={{ marginTop: 8, color: COLORS.muted, fontSize: FONT.description }}>
                            {collection.description}
                          </p>
                        )}
                      </Panel>
                    </FlexboxGrid.Item>
                  ))}
                </FlexboxGrid>
              </>
            )}
          </div>

          <CreateCollectionModal
            open={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            defaultValues={['DECK', 'TRADE_BINDER', 'WISHLIST'].includes(typeFilter) ? { type: typeFilter } : undefined}
            onCreated={(created) => {
              toaster.push(
                <Message type="success" showIcon closable>
                  <strong>{created.name}</strong> created successfully
                </Message>,
                { placement: 'topCenter', duration: 3000 }
              );
              navigate(`/library/${created.id}`);
            }}
          />

          <ImportCSVModal
            open={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImported={(data) => {
              navigate(`/library/${data.collectionId}`);
            }}
          />

          <ImportDeckModal
            open={showDeckImportModal}
            onClose={() => setShowDeckImportModal(false)}
            onSuccess={fetchCollections}
          />

          <FolderManagementModal
            open={showFolderModal}
            onClose={() => setShowFolderModal(false)}
            folders={folders}
            onFoldersChanged={setFolders}
          />

          <MoveDeckToFolderModal
            open={!!moveDeckTarget}
            onClose={() => setMoveDeckTarget(null)}
            deck={moveDeckTarget}
            folders={folders}
            onMoved={handleDeckMoved}
          />

          <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="xs">
            <Modal.Header>
              <Modal.Title>Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will
              remove all cards in this deck or binder and cannot be undone.
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setDeleteTarget(null)} appearance="subtle">
                Cancel
              </Button>
              <Button onClick={handleDelete} appearance="primary" color="red" loading={deleting}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </Content>
      </Container>
    </>
  );
};

export default Dashboard;
